const DEFAULT_ROOT_SELECTOR = '[data-scroll-snap-slider]'
const DEFAULT_TRACK_SELECTOR = '[data-scroll-snap-slider-track]'
const DEFAULT_PREV_SELECTOR = '[data-scroll-snap-slider-prev]'
const DEFAULT_NEXT_SELECTOR = '[data-scroll-snap-slider-next]'
const PREV_GRADIENT_SELECTOR = '[data-scroll-snap-slider-prev-gradient]'
const NEXT_GRADIENT_SELECTOR = '[data-scroll-snap-slider-next-gradient]'
const DEFAULT_EDGE_TOLERANCE = 8
const DEFAULT_SCROLL_RATIO = 0.85
const DEFAULT_UPDATE_DELAY = 250

function resolveOptions(options = {}) {
  return {
    rootSelector: options.rootSelector || DEFAULT_ROOT_SELECTOR,
    trackSelector: options.trackSelector || DEFAULT_TRACK_SELECTOR,
    prevSelector: options.prevSelector || DEFAULT_PREV_SELECTOR,
    nextSelector: options.nextSelector || DEFAULT_NEXT_SELECTOR,
    edgeTolerance: options.edgeTolerance ?? DEFAULT_EDGE_TOLERANCE,
    scrollRatio: options.scrollRatio ?? DEFAULT_SCROLL_RATIO,
    updateDelay: options.updateDelay ?? DEFAULT_UPDATE_DELAY,
    snapToItem: options.snapToItem ?? false,
    snapToItemRootSelector: options.snapToItemRootSelector || null,
    initializedDataKey: options.initializedDataKey || 'scrollSnapSliderInitialized',
    updateOnResize: options.updateOnResize ?? true,
  }
}

function sliderTrack(root, options) {
  return root.querySelector(options.trackSelector)
}

function canScrollBackward(track, options) {
  return track.scrollLeft > options.edgeTolerance
}

function canScrollForward(track, options) {
  return track.scrollLeft + track.clientWidth < track.scrollWidth - options.edgeTolerance
}

function sliderItems(track) {
  return Array.from(track.children).filter((item) => item instanceof HTMLElement)
}

function itemScrollLeft(track, item) {
  return track.scrollLeft + item.getBoundingClientRect().left - track.getBoundingClientRect().left
}

function scrollTrackWithFallback(track, methodName, left, fallback) {
  const scrollMethod = track[methodName]
  if (typeof scrollMethod === 'function') {
    try {
      scrollMethod.call(track, { left, behavior: 'smooth' })
      return
    } catch (error) {
      try {
        scrollMethod.call(track, left, 0)
        return
      } catch (fallbackError) {
        // Use the supplied direct scroll fallback below.
      }
    }
  }

  fallback()
}

function scrollTrackTo(track, left) {
  scrollTrackWithFallback(track, 'scrollTo', left, () => {
    track.scrollLeft = left
  })
}

function scrollTrackBy(track, left) {
  scrollTrackWithFallback(track, 'scrollBy', left, () => {
    track.scrollLeft += left
  })
}

function nextSnapItem(track, direction, options) {
  const items = sliderItems(track)
  if (direction > 0) {
    return items.find((item) => itemScrollLeft(track, item) > track.scrollLeft + options.edgeTolerance) ?? items[items.length - 1] ?? null
  }

  const previousItems = items.filter((item) => itemScrollLeft(track, item) < track.scrollLeft - options.edgeTolerance)
  return previousItems.length > 0 ? previousItems[previousItems.length - 1] : items[0] ?? null
}

function scrollToSliderItem(track, item) {
  scrollTrackTo(track, itemScrollLeft(track, item))
}

function shouldSnapToItem(root, options) {
  return options.snapToItem || (
    options.snapToItemRootSelector !== null &&
    root instanceof Element &&
    root.matches(options.snapToItemRootSelector)
  )
}

function sliderRootsIn(root, options) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return []
  }

  const sliderRoots = []
  if (root instanceof Element && root.matches(options.rootSelector)) {
    sliderRoots.push(root)
  }

  root.querySelectorAll(options.rootSelector).forEach((sliderRoot) => {
    sliderRoots.push(sliderRoot)
  })

  return sliderRoots
}

export function updateScrollSnapSliderControls(root, options = {}) {
  const resolvedOptions = resolveOptions(options)
  const track = sliderTrack(root, resolvedOptions)
  if (!(track instanceof HTMLElement)) {
    return
  }

  const previousButton = root.querySelector(resolvedOptions.prevSelector)
  const nextButton = root.querySelector(resolvedOptions.nextSelector)
  const previousGradient = root.querySelector(PREV_GRADIENT_SELECTOR)
  const nextGradient = root.querySelector(NEXT_GRADIENT_SELECTOR)
  const canMoveBackward = canScrollBackward(track, resolvedOptions)
  const canMoveForward = canScrollForward(track, resolvedOptions)

  if (previousButton instanceof HTMLButtonElement && previousButton.disabled !== !canMoveBackward) {
    previousButton.disabled = !canMoveBackward
  }

  if (nextButton instanceof HTMLButtonElement && nextButton.disabled !== !canMoveForward) {
    nextButton.disabled = !canMoveForward
  }

  if (previousGradient instanceof HTMLElement) {
    previousGradient.classList.toggle('opacity-0', !canMoveBackward)
  }

  if (nextGradient instanceof HTMLElement) {
    nextGradient.classList.toggle('opacity-0', !canMoveForward)
  }
}

export function scrollSnapSlider(root, direction, options = {}) {
  const resolvedOptions = resolveOptions(options)
  const track = sliderTrack(root, resolvedOptions)
  if (!(track instanceof HTMLElement)) {
    return
  }

  if (shouldSnapToItem(root, resolvedOptions)) {
    const targetItem = nextSnapItem(track, direction, resolvedOptions)
    if (targetItem !== null) {
      scrollToSliderItem(track, targetItem)
      window.setTimeout(() => updateScrollSnapSliderControls(root, resolvedOptions), resolvedOptions.updateDelay)
      return
    }
  }

  const scrollDistance = direction * Math.max(track.clientWidth * resolvedOptions.scrollRatio, 1)
  scrollTrackBy(track, scrollDistance)

  window.setTimeout(() => updateScrollSnapSliderControls(root, resolvedOptions), resolvedOptions.updateDelay)
}

function initSliderRoot(root, options) {
  if (!(root instanceof HTMLElement)) {
    return
  }

  if (root.dataset[options.initializedDataKey] === 'true') {
    return
  }

  root.dataset[options.initializedDataKey] = 'true'
  updateScrollSnapSliderControls(root, options)

  const track = sliderTrack(root, options)
  if (track instanceof HTMLElement) {
    track.addEventListener('scroll', () => updateScrollSnapSliderControls(root, options), { passive: true })
  }

  const previousButton = root.querySelector(options.prevSelector)
  if (previousButton instanceof HTMLButtonElement) {
    previousButton.addEventListener('click', (event) => {
      event.preventDefault()
      scrollSnapSlider(root, -1, options)
    })
  }

  const nextButton = root.querySelector(options.nextSelector)
  if (nextButton instanceof HTMLButtonElement) {
    nextButton.addEventListener('click', (event) => {
      event.preventDefault()
      scrollSnapSlider(root, 1, options)
    })
  }

  if (options.updateOnResize) {
    window.addEventListener('resize', () => updateScrollSnapSliderControls(root, options))
  }
}

// Use the default data-scroll-snap-slider* attributes for new sliders, or pass page-specific selectors for existing markup.
export function initScrollSnapSlider(root = document, options = {}) {
  const resolvedOptions = resolveOptions(options)
  sliderRootsIn(root, resolvedOptions).forEach((sliderRoot) => {
    initSliderRoot(sliderRoot, resolvedOptions)
  })
}
